import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import * as CoreReducer from '../reducers';
import * as NotificationActions from '../actions/notification.actions';
import {catchError, exhaustMap, switchMap, tap, withLatestFrom} from 'rxjs/operators';
import { of} from 'rxjs';
import {NotificationService} from '../../../shared/services/notification.service';
import {NotificationStats} from '../../../shared/models/notification-stats';
import {Notification} from '../../../shared/models/notification';
import {Store} from '@ngrx/store';
import {UpdateNotification} from '../../../shared/models/update-notification';
import {NotificationStatus} from '../../../shared/models/notification-status';
import { LoadingBarState } from '@ngx-loading-bar/core/loading-bar.state';
import { LoadingBarService } from '@ngx-loading-bar/core';

@Injectable()
export class NotificationsEffects {

  notificationsInit$ = createEffect(() =>
    this._actions$.pipe(
      ofType(NotificationActions.notificationsInit.type),
      tap(action => {
        this._notificationService.init();
      })
    ),
    { dispatch: false }
  );

  notificationsDisconnect$ = createEffect(() =>
    this._actions$.pipe(
      ofType(NotificationActions.notificationsDisconnect.type),
      tap(action => {
        this._notificationService.disconnect();
      })
    ),
    { dispatch: false }
  );

  getNotifications$ = createEffect(() =>
    this._actions$.pipe(
      ofType(NotificationActions.getNotifications.type),
      switchMap((action: any) => this._notificationService.getNotifications(action.page)
        .pipe(
          exhaustMap((data) => {
            const stats = new NotificationStats(data.collectionMetadata.unreadNotifications, data.currentPage, data.totalPages );
            const notifications = this._notificationService.update(data.items);
            return of(NotificationActions.getNotificationsSuccess({notifications: notifications, stats: stats}));
          }),
          catchError(error => of(NotificationActions.getNotificationsFailure({error})))
        )
      )
    )
  );

  getNotificationByIds$ = createEffect(() =>
    this._actions$.pipe(
      ofType(NotificationActions.getNotificationByIds.type),
      withLatestFrom(
        this._store$.select(CoreReducer.getNotifications),
      ),
      switchMap(([action, notifications]) => this._notificationService.getNotificationByIds(action['ids'])
        .pipe(
          exhaustMap((data) => {
            const list: Notification[] = Object.assign([], notifications);
            const newNotifications = this._notificationService.update(data.items);
            newNotifications.forEach(x => {
              const oldNotificationIndex = list.findIndex(n => n.notificationId === x.notificationId);
              if (oldNotificationIndex > -1) {
                list[oldNotificationIndex] = x;
              } else {
                list.push(x);
              }
            });
            return of(NotificationActions.getNotificationByIdsSuccess({notifications: this._notificationService.sortNotifications(list)}));
          }),
          catchError(error => of(NotificationActions.getNotificationByIdsFailure({error})))
        )
      )
    )
  );

  loadMoreNotifications$ = createEffect(() =>
    this._actions$.pipe(
      ofType(NotificationActions.loadMoreNotifications.type),
      withLatestFrom(
        this._store$.select(CoreReducer.getNotificationStats),
        this._store$.select(CoreReducer.getNotifications),
      ),
      tap(() => this._loader.start()),
      switchMap(([action, stats, notifications]) => this._notificationService.getNotifications(stats.currentPage + 1)
        .pipe(
          exhaustMap((data) => {
            const newStats = new NotificationStats(data.collectionMetadata.unreadNotifications, data.currentPage, data.totalPages );
            const list = Object.assign([], notifications);
            const newNotifications = this._notificationService.update(data.items);
            list.push(...newNotifications);

            return of(NotificationActions.loadMoreNotificationsSuccess({notifications: this._notificationService.sortNotifications(list), stats: newStats}));
          }),
          catchError(error => of(NotificationActions.loadMoreNotificationsFailure({error}))),
          tap(() => this._loader.complete()),
        )
      )
    )
  );


  updateNotificationsStatus$ = createEffect(() =>
    this._actions$.pipe(
      ofType(NotificationActions.updateNotificationsStatus.type),
      tap(() => this._loader.start()),
      switchMap((action: any) => this._notificationService.updateNotificationStatus(new UpdateNotification(action.notificationsIds, NotificationStatus.READ))
        .pipe(
          exhaustMap((data) => of(NotificationActions.updateNotificationsStatusSuccess({notifications: data}))),
          catchError(error => of(NotificationActions.updateNotificationsStatusFailure({error}))),
          tap(() => this._loader.complete()),
        )
      )
    )
  );

  pushNotification$ = createEffect(() =>
    this._actions$.pipe(
      ofType(NotificationActions.pushNotification.type),
      withLatestFrom(
        this._store$.select(CoreReducer.getNotifications),
      ),
      switchMap(([action, notifications]) => {
        const list = Object.assign([], notifications);
        list.push(...this._notificationService.update([action['notification']]));
        return of(NotificationActions.pushNotificationSuccess({notifications: this._notificationService.sortNotifications(list)}));
        }
      )
    )
  );

  private _loader: LoadingBarState;

  constructor(
    private _actions$: Actions,
    private _store$: Store<any>,
    private _notificationService: NotificationService,
    private _loadingBar: LoadingBarService
  ) {
    this._loader = this._loadingBar.useRef('main');
  }
}
